import React, { useState } from 'react';
import Modal from '@components/molecules/modal/modal';
import Typography from '@components/atoms/typography/typography';
import { useTranslation } from 'next-i18next';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { createZendeskTicket } from '@services/my-orders-sales-service';
import { OutlineButtonView, OkButtonView } from '../common';
import styles from './select-carrier-options.module.scss';

const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';
const CLASSNAME = 'getShippingLabelModal';

interface SubmitErrorModalProps {
  onClose: () => void;
  errorMessage: string;
  errorObjects: unknown[];
  productId: string;
  onGenerateReviewAddress?: () => void;
  onTicketSupportSent: () => void;
}

export const SubmitErrorModal = ({
  onClose,
  onGenerateReviewAddress,
  errorMessage,
  errorObjects,
  productId,
  onTicketSupportSent,
}: SubmitErrorModalProps) => {
  const { t } = useTranslation('orders-sales');

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const [isLoading, setIsLoading] = useState(false);

  const reviewAddress = () => {
    sendAnalyticEvent('pop_in_issue_generating_label_cta', { action: 'review_address' });
    onGenerateReviewAddress();
  };

  const contactUs = async () => {
    sendAnalyticEvent('pop_in_issue_generating_label_cta', { action: 'contact_us' });
    try {
      setIsLoading(true);
      await createZendeskTicket(productId);
      onTicketSupportSent();
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const havingSenderAddressError =
    Array.isArray(errorObjects) &&
    errorObjects.find((error) => (error as { code?: string } | undefined)?.code === 'SENDER_ADDRESS_ERROR');

  const havingZendeskTicket =
    Array.isArray(errorObjects) &&
    errorObjects.find((error) => (error as { code?: string } | undefined)?.code === 'CUSTOMER_TICKET');

  return (
    <Modal
      open={true}
      title={t(`${LANG_PREFIX}GENERATION_FAILED`)}
      onClose={onClose}
      className={styles[`${CLASSNAME}__modalError`]}
    >
      <div className={styles[`${CLASSNAME}__modalError__container`]}>
        <Typography
          variant="body2"
          className={styles[`${CLASSNAME}__modalError__failedDescription`]}
        >
          {t(`${LANG_PREFIX}GENERATION_FAILED_DESCRIPTION`)}
        </Typography>

        {havingZendeskTicket && (
          <Typography
            variant="body2"
            className={styles[`${CLASSNAME}__modalError__description--zendesk`]}
          >
            {t(`${LANG_PREFIX}GENERATION_FAILED_DESCRIPTION_ZENDESK`)}
          </Typography>
        )}

        <Typography
          variant="body2"
          className={styles[`${CLASSNAME}__modalError__description`]}
        >
          {errorMessage || t('MY_ACCOUNT.ORDERS_SALES.ERROR.API')}
        </Typography>

        <div className={styles[`${CLASSNAME}__modalError__buttons`]}>
          {havingSenderAddressError && !!onGenerateReviewAddress && (
            <OkButtonView
              isSubmitting={false}
              onClick={reviewAddress}
            >
              {t(`${LANG_PREFIX}GENERATE_REVIEW_ADDRESS`)}
            </OkButtonView>
          )}

          <OutlineButtonView
            withLoader={true}
            loaded={!isLoading}
            disabled={isLoading || !!havingZendeskTicket}
            onClick={contactUs}
          >
            {t(`${LANG_PREFIX}GENERATE_CONTACT_US`)}
          </OutlineButtonView>
        </div>
      </div>
    </Modal>
  );
};
